import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { 
  Container, 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  CircularProgress,
  Grid,
  TextField,
  Button,
  Box,
  Snackbar,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormLabel,
  Tabs,
  Tab,
  Select,
  InputLabel,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Autocomplete,
  TablePagination,
  InputAdornment
} from '@mui/material';
import { 
  Edit as EditIcon, 
  Delete as DeleteIcon,
  PhotoCamera as PhotoCameraIcon,
  CloudUpload as CloudUploadIcon,
  CameraAlt as CameraAltIcon,
  Check as CheckIcon,
  Add as AddIcon,
  Search as SearchIcon
} from '@mui/icons-material';

import { imageURL } from '../../../imageURL'; // Correct relative path
import { resolvePledgePhotoUrl } from '../../../utils/pledgePhotoUrl';
import axios from 'axios';
import baseURL from '../../../baseURL';
import MasterPageShell, {
  masterPaperSx,
  masterListPaperSx,
  masterPrimaryBtnSx,
  masterFieldSx,
  masterTableHeadCellSx,
} from '../../common/MasterPageShell';
import { DHARA_OPTIONS, DEFAULT_DHARA } from '../../../constants/dhara';

import { compressImageToFile } from '../../../utils/compressImage';

function pickGoldArticleRate(articles) {
  if (!Array.isArray(articles)) return null;
  let best = null;
  let bestScore = -1;
  for (const article of articles) {
    const title = String(article.title || '').toLowerCase();
    if (!title.includes('gold')) continue;
    let score = 50;
    if (title === 'gold') score = 100;
    else if (title.includes('ornament')) score = 90;
    else if (title.includes('coin')) score = 70;
    if (score > bestScore) {
      bestScore = score;
      best = article;
    }
  }
  if (!best || best.percentage == null) return null;
  const n = Number(best.percentage);
  return Number.isFinite(n) ? n : null;
}

// Helper function to compress image to dataURL (for localStorage)
const compressImage = (file, maxWidth = 800, maxHeight = 800, quality = 0.7) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        // Calculate new dimensions
        if (width > height) {
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (blob) {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result);
              reader.onerror = reject;
              reader.readAsDataURL(blob);
            } else {
              reject(new Error('Failed to compress image'));
            }
          },
          'image/jpeg',
          quality
        );
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

/** Option B: each newline-separated segment is split into chunks of maxLen (no total length limit). */
const ADDRESS_LINE_MAX_CHARS = 30;

function wrapAddressPerLineSegments(value, maxLen = ADDRESS_LINE_MAX_CHARS) {
  if (value == null || value === '') return '';
  return value
    .split('\n')
    .map((segment) => {
      if (segment.length <= maxLen) return segment;
      const parts = [];
      for (let i = 0; i < segment.length; i += maxLen) {
        parts.push(segment.slice(i, i + maxLen));
      }
      return parts.join('\n');
    })
    .join('\n');
}

// Helper function to save to localStorage with error handling
const saveToLocalStorage = (key, value) => {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (e) {
    if (e.name === 'QuotaExceededError' || e.name === 'NS_ERROR_DOM_QUOTA_REACHED') {
      // Clear old photos and try again
      try {
        const existingPhotos = JSON.parse(localStorage.getItem('ledgerPhotos') || '{}');
        // Keep only the most recent photo for each type
        const photoKeys = Object.keys(existingPhotos);
        if (photoKeys.length > 0) {
          // Clear all except the current one being saved
          const photosToKeep = {};
          photoKeys.forEach(key => {
            if (key !== 'ledgerPhotos') {
              // Try to keep only essential photos
              photosToKeep[key] = existingPhotos[key];
            }
          });
          localStorage.setItem('ledgerPhotos', JSON.stringify(photosToKeep));
          // Try saving again
          localStorage.setItem(key, value);
          return true;
        } else {
          // If still fails, clear everything and save
          localStorage.clear();
          localStorage.setItem(key, value);
          return true;
        }
      } catch (e2) {
        return false;
      }
    }
    throw e;
  }
};

const PhotoUploadComponent = ({ 
  photoType, 
  onPhotoCapture, 
  onFileUpload,
  existingPhotos // Add this prop to pass existing photos
}) => {
  // Refs with explicit initialization
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const dialogRef = useRef(null);
  
  // State variables
  const [openCamera, setOpenCamera] = useState(false);
  const [currentStream, setCurrentStream] = useState(null);
  const [isDialogReady, setIsDialogReady] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [cameraError, setCameraError] = useState(null);
  const [photoStatus, setPhotoStatus] = useState({
    captured: false,
    confirmed: false
  });

  // Enhanced camera initialization
  const startCamera = useCallback(async () => {
    // Reset previous states
    setCameraError(null);
    setIsDialogReady(false);

    try {
      // Comprehensive browser support check
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Camera access not supported in this browser');
      }

      // Prepare constraints with specific requirements
      const constraints = {
        video: {
          width: { ideal: 1280, max: 1920 },
          height: { ideal: 720, max: 1080 },
          facingMode: 'environment'  // Prefer back camera
        }
      };

      // Request camera access
      const stream = await navigator.mediaDevices.getUserMedia(constraints);

      // Ensure video ref is set and is a valid DOM element
      if (!videoRef.current || !(videoRef.current instanceof HTMLVideoElement)) {
        throw new Error('Invalid video reference');
      }

      // Set video source and play
      videoRef.current.srcObject = stream;
      
      // Wait for metadata to load
      await new Promise((resolve, reject) => {
        videoRef.current.onloadedmetadata = () => {
          videoRef.current.play()
            .then(() => {
              resolve();
              setIsDialogReady(true);
            })
            .catch(reject);
        };
        videoRef.current.onerror = reject;
      });

      // Update state
      setCurrentStream(stream);
      setOpenCamera(true);

    } catch (error) {

      // Detailed error messaging
      let userFriendlyMessage = 'Failed to access camera';
      switch (error.name) {
        case 'NotAllowedError':
          userFriendlyMessage = 'Camera access denied. Please check browser permissions.';
          break;
        case 'NotFoundError':
          userFriendlyMessage = 'No camera found. Please connect a camera.';
          break;
        case 'OverconstrainedError':
          userFriendlyMessage = 'Camera does not meet required specifications.';
          break;
      }

      setCameraError(userFriendlyMessage);
      setOpenCamera(false);
    }
  }, []);

  // Open camera handler with additional checks
  const handleOpenCamera = () => {
    // Ensure refs are properly set
    if (!videoRef.current) {
      videoRef.current = document.createElement('video');
      videoRef.current.setAttribute('playsinline', '');
      videoRef.current.setAttribute('autoplay', '');
    }

    if (!canvasRef.current) {
      canvasRef.current = document.createElement('canvas');
    }

    // Open camera and initialize
    setOpenCamera(true);
    startCamera();
  };

  // Capture photo method
  const capturePhoto = () => {
    try {
      // Ensure video and canvas refs exist
      if (!videoRef.current || !canvasRef.current) {
        return;
      }

      // Get video dimensions
      const video = videoRef.current;
      const canvas = canvasRef.current;
      
      // Set canvas to same dimensions as video
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      // Draw current video frame to canvas
      const context = canvas.getContext('2d');
      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      // Convert canvas to data URL (base64 image)
      const capturedImageDataUrl = canvas.toDataURL('image/jpeg');

      // Stop video stream
      if (currentStream) {
        currentStream.getTracks().forEach(track => track.stop());
      }

      // Immediately process and save the image
      fetch(capturedImageDataUrl)
        .then(res => res.blob())
        .then(blob => {
          const file = new File([blob], `${photoType}_capture.jpg`, { type: 'image/jpeg' });
          
          // Compress image for upload (<100KB for cloud) and for localStorage
          Promise.all([
            compressImageToFile(file, photoType === 'clientPhoto' || photoType === 'aadharCard' || photoType === 'panCard' ? 2 : 100, 320, 320, 0.5),
            compressImage(file) // Compress to dataURL for localStorage
          ])
            .then(([compressedFile, compressedDataUrl]) => {
              // Save to localStorage
              const existingPhotos = JSON.parse(localStorage.getItem('ledgerPhotos') || '{}');
              existingPhotos[photoType] = compressedDataUrl;
              const saved = saveToLocalStorage('ledgerPhotos', JSON.stringify(existingPhotos));
              
              if (saved) {
                // Update component state
                setPreviewImage(compressedDataUrl);
                setPhotoStatus({
                  captured: true,
                  confirmed: true
                });
              } else {
                setCameraError('Failed to save photo. Storage quota exceeded. Please clear some photos.');
              }
              
              // Call onFileUpload with compressed file (for server upload)
              if (onFileUpload) {
                onFileUpload(compressedFile, photoType);
              }
            })
            .catch(error => {
              setCameraError('Failed to process image');
            });
          
          // Close camera dialog
          setOpenCamera(false);
        })
        .catch(error => {
          setCameraError('Failed to process captured image');
        });

    } catch (error) {
      setCameraError('Failed to capture photo');
    }
  };

  // File upload handler
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const isPdf = file.type === 'application/pdf';
      const maxKb =
        !isPdf && (photoType === 'clientPhoto' || photoType === 'aadharCard' || photoType === 'panCard')
          ? 2
          : 100;
      Promise.all([
        isPdf ? Promise.resolve(file) : compressImageToFile(file, maxKb, 320, 320, 0.5),
        isPdf ? Promise.resolve(null) : compressImage(file)
      ])
        .then(([compressedFile, compressedDataUrl]) => {
          // Save to localStorage
          const existingPhotos = JSON.parse(localStorage.getItem('ledgerPhotos') || '{}');
          existingPhotos[photoType] = compressedDataUrl;
          const saved = saveToLocalStorage('ledgerPhotos', JSON.stringify(existingPhotos));
          
          if (saved) {
            // Update component state
            setPreviewImage(compressedDataUrl);
            setPhotoStatus({
              captured: true,
              confirmed: true
            });
          } else {
            setCameraError('Failed to save photo. Storage quota exceeded. Please clear some photos.');
          }
          
          // Call file upload callback with compressed file (for server upload)
          if (onFileUpload) {
            onFileUpload(compressedFile, photoType);
          }
        })
        .catch(error => {
          setCameraError('Failed to process image');
        });
    }
  };

  // Effect to load existing photos from localStorage
  useEffect(() => {
    // Load photos from localStorage when component mounts or photoType changes
    const savedPhotos = JSON.parse(localStorage.getItem('ledgerPhotos') || '{}');
    
    // Prioritize order: 
    // 1. Saved photos in localStorage
    // 2. Existing photos passed as prop
    // 3. Null if no photos exist
    const photoToDisplay = 
      savedPhotos[photoType] || 
      (existingPhotos && existingPhotos[photoType] ? existingPhotos[photoType] : null);
    
    // Set preview image
    setPreviewImage(photoToDisplay);
    
    // Update photo status based on photo existence
    setPhotoStatus({
      captured: !!photoToDisplay,
      confirmed: !!photoToDisplay
    });
  }, [photoType, existingPhotos]);

  // Reset camera and preview states when photoType changes
  useEffect(() => {
    // Close camera if open
    setOpenCamera(false);
    
    // Clear any previous camera errors
    setCameraError(null);
  }, [photoType]);

  // Reset photo previews across different photo types
  const resetPhotoPreviews = () => {
    // Reset preview images
    setPreviewImage(null);
    
    // Reset photo status
    setPhotoStatus({
      captured: false,
      confirmed: false
    });
  };

  return (
    <Box 
      ref={dialogRef}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        border: '2px dashed',
        borderColor: 'grey.300',
        borderRadius: 2,
        p: 2,
        textAlign: 'center',
        width: '100%',
        position: 'relative',
        minHeight: 250
      }}
    >
      {/* File Input */}
      <input
        ref={fileInputRef}
        accept="image/*"
        style={{ display: 'none' }}
        id={`${photoType}-file-input`}
        type="file"
        onChange={handleFileUpload}
      />
      
      {/* Preview Image or Placeholder */}
      {previewImage && (
        <Box sx={{ position: 'relative', width: '100%', maxHeight: 200 }}>
          <img 
            src={previewImage} 
            alt="Preview" 
            style={{ 
              maxWidth: '100%', 
              maxHeight: 200, 
              objectFit: 'contain' 
            }} 
            onError={(e) => {
              e.target.onerror = null; // Prevent infinite loop
              e.target.src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0Ij48cGF0aCBkPSJNMTIgMkM2LjQ4IDIgMiA2LjQ4IDIgMTJzNC40OCAxMCAxMCAxMCAxMC00LjQ4IDEwLTEwUzE3LjUyIDIgMTIgMnptMSAxNWgtMnYtMmgydjJ6bTAtNGgtMlY3aDJ2NnoiLz48L3N2Zz4=';
            }} 
          />
          <IconButton
            color="error"
            size="small"
            sx={{ 
              position: 'absolute', 
              top: 0, 
              right: 0 
            }}
            onClick={() => {
              // Remove from localStorage
              const existingPhotos = JSON.parse(localStorage.getItem('ledgerPhotos') || '{}');
              delete existingPhotos[photoType];
              localStorage.setItem('ledgerPhotos', JSON.stringify(existingPhotos));
              
              // Clear preview
              setPreviewImage(null);
              setPhotoStatus({
                captured: false,
                confirmed: false
              });
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      )}
      
      {/* Action Buttons */}
      <Box 
        sx={{ 
          display: 'flex', 
          gap: 1, 
          mt: 2, 
          width: '100%', 
          justifyContent: 'center' 
        }}
      >
        {/* File Upload Button */}
        <label htmlFor={`${photoType}-file-input`}>
          <Button 
            variant="outlined" 
            component="span" 
            startIcon={<CloudUploadIcon fontSize="small" />}
            color="primary"
            size="small"
          >
            Upload
          </Button>
        </label>

        {/* Camera Capture Button */}
        <Button 
          variant="outlined" 
          color="secondary" 
          startIcon={<CameraAltIcon fontSize="small" />}
          onClick={handleOpenCamera}
          size="small"
        >
          Capture
        </Button>
      </Box>

      {/* Camera Capture Dialog */}
      <Dialog 
        open={openCamera} 
        onClose={() => {
          if (currentStream) {
            currentStream.getTracks().forEach(track => track.stop());
          }
          setOpenCamera(false);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Capture {photoType.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
        </DialogTitle>
        <DialogContent>
          {cameraError ? (
            <Alert severity="error">{cameraError}</Alert>
          ) : (
            <Box 
              sx={{ 
                position: 'relative', 
                width: '100%', 
                paddingTop: '56.25%', // 16:9 aspect ratio
                backgroundColor: 'black' 
              }}
            >
              <video 
                ref={videoRef} 
                playsInline
                autoPlay
                style={{ 
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  backgroundColor: 'black'
                }} 
              />
              <canvas 
                ref={canvasRef} 
                style={{ display: 'none' }} 
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            if (currentStream) {
              currentStream.getTracks().forEach(track => track.stop());
            }
            setOpenCamera(false);
          }}>Cancel</Button>
          <Button 
            onClick={capturePhoto} 
            disabled={!!cameraError}
          >
            Capture
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`photo-tabpanel-${index}`}
      aria-labelledby={`photo-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const LedgerMaster = ({ isModal, onCustomerCreated, onClose }) => {
  // Ensure body scroll is restored when entering Ledger (avoids stuck state from other screens)
  useEffect(() => {
    document.body.style.overflow = 'unset';
    return () => { document.body.style.overflow = 'unset'; };
  }, []);

  const [idTypes, setIdTypes] = useState([]);
  const [selectedIdTypes, setSelectedIdTypes] = useState([]);
  const [modalIdPicks, setModalIdPicks] = useState([]);
  const [showOtherFields, setShowOtherFields] = useState(false);
  const [otherIdName, setOtherIdName] = useState('');
  const [otherIdNumber, setOtherIdNumber] = useState('');
  const [idNumbers, setIdNumbers] = useState({
    aadhar: '',
    pan: '',
    voter: '',
    driving: ''
  });

  // Add New Caste Modal State
  const [addCasteModalOpen, setAddCasteModalOpen] = useState(false);
  const [newCasteForm, setNewCasteForm] = useState({ name: '' });
  const [casteLoading, setCasteLoading] = useState(false);

  // Add New Village Modal State
  const [addVillageModalOpen, setAddVillageModalOpen] = useState(false);
  const [newVillageForm, setNewVillageForm] = useState({
    village: '',
    atPost: '',
    taluka: '',
    district: ''
  });
  
  // Ensure form values are never undefined
  const resetVillageForm = () => {
    setNewVillageForm({
      village: '',
      atPost: '',
      taluka: '',
      district: ''
    });
  };
  
  // Add effect to track modal state changes
  useEffect(() => {
    if (addVillageModalOpen) {
    } else {
    }
  }, [addVillageModalOpen, newVillageForm]);
  
  // Add effect to track newVillageForm changes
  useEffect(() => {
  }, [newVillageForm]);
  const [villageLoading, setVillageLoading] = useState(false);



  const idTypeOptions = [
    { value: 'aadhar', label: 'Aadhar Card' },
    { value: 'pan', label: 'Pancard' },
    { value: 'voter', label: 'Voter ID' },
    { value: 'driving', label: 'Driving Licence' },
    { value: 'other', label: 'Other' }
  ];

  const handleIdTypeChange = (event) => {
    const selectedValues = event.target.value;
    setSelectedIdTypes(selectedValues);
    // Show other fields only if 'other' is selected
    setShowOtherFields(selectedValues.includes('other'));
  };

  const MODAL_ID_OPTIONS = [
    { value: 'client', label: 'Client Photo', photo: 'clientPhoto' },
    { value: 'aadhar', label: 'Aadhar Card', photo: 'aadharCard', numberKey: 'aadhar', numberLabel: 'Aadhar Number' },
    { value: 'pan', label: 'PAN Card', photo: 'panCard', numberKey: 'pan', numberLabel: 'PAN Number' },
    { value: 'driving', label: 'Driving Licence', numberKey: 'driving', numberLabel: 'DL Number' },
    { value: 'voter', label: 'Voter ID', numberKey: 'voter', numberLabel: 'Voter ID Number' },
    { value: 'other', label: 'Other' }
  ];

  const handleModalIdPicks = (event) => {
    const raw = event.target.value;
    const next = typeof raw === 'string' ? raw.split(',') : raw;
    setModalIdPicks(next);
    setSelectedIdTypes(next.filter((value) => value !== 'client'));
    setShowOtherFields(next.includes('other'));
  };

  const handleOtherIdNameChange = (event) => {
    setOtherIdName(event.target.value);
  };

  const handleOtherIdNumberChange = (event) => {
    setOtherIdNumber(event.target.value);
  };

  const handleIdNumberChange = (field) => (event) => {
    const upperValue = event.target.value.toUpperCase();
    const newIdNumbers = { ...idNumbers };
    newIdNumbers[field] = upperValue;
    setIdNumbers(newIdNumbers);
  };

  // Add New Caste Handlers
  const handleAddCaste = async () => {
    if (!newCasteForm.name.trim()) {
      setCreateError('Caste name is required');
      return;
    }

    setCasteLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${baseURL}/castes`, 
        { name: newCasteForm.name.toUpperCase() },
        { 
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          } 
        }
      );

      // Add new caste to the list (response.data.data contains the created caste)
      setCastes(prev => [response.data.data, ...prev]);
      
      // Select the newly created caste
      setLedgerForm(prev => ({
        ...prev,
        caste: response.data.data.name
      }));

      // Close modal and reset form
      setAddCasteModalOpen(false);
      setNewCasteForm({ name: '' });
      setCreateSuccess(true);
    } catch (err) {
      setCreateError(err.response?.data?.message || 'Failed to create caste');
    } finally {
      setCasteLoading(false);
    }
  };

  // Add New Village Handlers
  const handleAddVillage = async () => {
    
    if (!newVillageForm.village.trim() || !newVillageForm.atPost.trim() || 
        !newVillageForm.taluka.trim() || !newVillageForm.district.trim()) {
      setCreateError('All village fields are required');
      return;
    }

    setVillageLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${baseURL}/town-villages`, 
        {
          village: newVillageForm.village.toUpperCase(),
          atPost: newVillageForm.atPost.toUpperCase(),
          taluka: newVillageForm.taluka.toUpperCase(),
          district: newVillageForm.district.toUpperCase()
        },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      // Add new village to the list (response.data.data contains the created village)
      setTownVillages(prev => [response.data.data, ...prev]);
      
      // Select the newly created village and auto-fill other fields (like caste)
      setLedgerForm(prev => ({
        ...prev,
        village: response.data.data.village,
        atPost: response.data.data.atPost,
        taluka: response.data.data.taluka,
        district: response.data.data.district
      }));

      // Close modal and reset form
      setAddVillageModalOpen(false);
      resetVillageForm();
      setCreateSuccess(true);
    } catch (err) {
      setCreateError(err.response?.data?.message || 'Failed to create village');
    } finally {
      setVillageLoading(false);
    }
  };

  

  const [ledgers, setLedgers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Ledger list pagination
  const [ledgerPage, setLedgerPage] = useState(0);
  const [ledgerRowsPerPage, setLedgerRowsPerPage] = useState(50);
  const [ledgerTotalDocs, setLedgerTotalDocs] = useState(0);
  const [ledgerSearch, setLedgerSearch] = useState('');

  // Ledger Form State
  const initialLedgerForm = {
    _id: '',
    type: 'Customer',
    firstName: '',
    lastName: '',
    relation: '',
    relationName: '',
    address: '',
    caste: '',
    occupation: '',
    mobileNo: '',
    village: '',
    atPost: '',
    taluka: '',
    district: '',
    panCardNo: '',
    aadharCardNo: '',
    groupCode: '',
    openingBalanceType: '',
    openingBalance: '',
    rateOfInterest: '',
    dhara: DEFAULT_DHARA,
    photos: {
      aadharCard: null,
      panCard: null,
      clientPhoto: null
    }
  };
  
  // Ensure form values are never undefined
  const resetLedgerForm = () => {
    setLedgerForm(initialLedgerForm);
  };

  const [ledgerForm, setLedgerForm] = useState(initialLedgerForm);
  const [isEditing, setIsEditing] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createSuccess, setCreateSuccess] = useState(false);
  const [createError, setCreateError] = useState(null);

  // Delete Ledger State
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [ledgerToDelete, setLedgerToDelete] = useState(null);

  // Dropdown Data
  const [castes, setCastes] = useState([]);
  const [townVillages, setTownVillages] = useState([]);
  const [groupCodes, setGroupCodes] = useState([]);

  const [photoUploadTab, setPhotoUploadTab] = useState(0);

  // Unique occupation suggestions from existing ledgers (Customer type)
  const occupationSuggestions = React.useMemo(() => {
    const list = (ledgers || [])
      .filter((l) => l.occupation && String(l.occupation).trim())
      .map((l) => String(l.occupation).trim().toUpperCase());
    return [...new Set(list)].sort();
  }, [ledgers]);

  const sortedCastes = useMemo(
    () =>
      [...castes].sort((a, b) =>
        (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base', numeric: true })
      ),
    [castes]
  );

  const sortedGroupCodes = useMemo(
    () =>
      [...groupCodes].sort((a, b) => {
        const byName = (a.name || '').localeCompare(b.name || '', undefined, {
          sensitivity: 'base',
          numeric: true,
        });
        if (byName !== 0) return byName;
        return String(a.code || '').localeCompare(String(b.code || ''), undefined, {
          sensitivity: 'base',
          numeric: true,
        });
      }),
    [groupCodes]
  );

  const sortedTownVillagesByVillage = useMemo(
    () =>
      [...townVillages]
        .filter((tv) => tv.village && tv.village.trim() !== '')
        .sort((a, b) =>
          (a.village || '').localeCompare(b.village || '', undefined, { sensitivity: 'base' })
        ),
    [townVillages]
  );

  const uniqueVillagesSorted = useMemo(
    () =>
      [...new Set(townVillages.map((tv) => (tv.village || '').trim()).filter(Boolean))].sort((a, b) =>
        a.localeCompare(b, undefined, { sensitivity: 'base' })
      ),
    [townVillages]
  );

  const uniqueAtPostsSorted = useMemo(
    () =>
      [...new Set(townVillages.map((tv) => (tv.atPost || '').trim()).filter(Boolean))].sort((a, b) =>
        a.localeCompare(b, undefined, { sensitivity: 'base' })
      ),
    [townVillages]
  );

  const uniqueTalukasSorted = useMemo(
    () =>
      [...new Set(townVillages.map((tv) => (tv.taluka || '').trim()).filter(Boolean))].sort((a, b) =>
        a.localeCompare(b, undefined, { sensitivity: 'base' })
      ),
    [townVillages]
  );

  const uniqueDistrictsSorted = useMemo(
    () =>
      [...new Set(townVillages.map((tv) => (tv.district || '').trim()).filter(Boolean))].sort((a, b) =>
        a.localeCompare(b, undefined, { sensitivity: 'base' })
      ),
    [townVillages]
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        
        const [ledgersResponse, castesResponse, townVillagesResponse, groupCodesResponse] = await Promise.all([
          axios.get(`${baseURL}/ledgers`, { params: { page: 1, limit: ledgerRowsPerPage }, headers: { 'Authorization': `Bearer ${token}` } }),
          axios.get(`${baseURL}/castes`, { params: { limit: 10000 }, headers: { 'Authorization': `Bearer ${token}` } }),
          axios.get(`${baseURL}/town-villages`, { 
            params: { limit: 10000 }, // Get ALL villages
            headers: { 'Authorization': `Bearer ${token}` } 
          }),
          axios.get(`${baseURL}/group-codes`, { params: { limit: 10000 }, headers: { 'Authorization': `Bearer ${token}` } })
        ]);


        // Update to handle different response structures
        const ledgerRes = ledgersResponse.data;
        setLedgers(ledgerRes.data || ledgerRes);
        setLedgerTotalDocs(ledgerRes.totalDocs ?? (ledgerRes.data || ledgerRes).length);
        setLedgerPage(0);
        
        // Handle castes response
        const casteData = castesResponse.data.data || castesResponse.data;
        setCastes(Array.isArray(casteData) ? casteData : []);
        
        // Handle town villages response
        const townVillageData = townVillagesResponse.data.data || townVillagesResponse.data;
        setTownVillages(Array.isArray(townVillageData) ? townVillageData : []);
        
        // Handle group codes response
        const groupCodeData = groupCodesResponse.data.data || groupCodesResponse.data;
        setGroupCodes(Array.isArray(groupCodeData) ? groupCodeData : []);
        
        setLoading(false);
      } catch (err) {
        setError(`Failed to fetch data: ${err.message}`);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const fetchLedgersPage = async (page = 0, rowsPerPage = ledgerRowsPerPage, search = ledgerSearch) => {
    try {
      const token = localStorage.getItem('token');
      const params = { page: page + 1, limit: rowsPerPage };
      const q = String(search ?? '').trim();
      if (q) params.search = q;
      const response = await axios.get(`${baseURL}/ledgers`, {
        params,
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const res = response.data;
      setLedgers(res.data || res);
      setLedgerTotalDocs(res.totalDocs ?? (res.data || res).length);
      setLedgerPage(page);
      setLedgerRowsPerPage(rowsPerPage);
    } catch (err) {
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchLedgersPage(0, ledgerRowsPerPage, ledgerSearch);
    }, 350);
    return () => clearTimeout(timer);
  }, [ledgerSearch]);

  const handleLedgerPageChange = (event, newPage) => {
    fetchLedgersPage(newPage, ledgerRowsPerPage, ledgerSearch);
  };

  const handleLedgerRowsPerPageChange = (event) => {
    const newRows = parseInt(event.target.value, 10);
    setLedgerRowsPerPage(newRows);
    fetchLedgersPage(0, newRows, ledgerSearch);
  };

  useEffect(() => {
    const handleBeforeUnload = () => {
      // Remove ledger-related photos from localStorage
      localStorage.removeItem('ledgerPhotos');
    };

    // Add event listener for page refresh/close
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Cleanup event listener
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    if (ledgerForm.village) {
      const matchingTownVillage = townVillages.find(tv => 
        tv.village && tv.village.trim().toUpperCase() === ledgerForm.village.trim().toUpperCase()
      );
      
      if (matchingTownVillage) {
        setLedgerForm(prev => ({
          ...prev,
          atPost: matchingTownVillage.atPost,
          taluka: matchingTownVillage.taluka,
          district: matchingTownVillage.district
        }));
      }
    }
  }, [ledgerForm.village, townVillages]);

  useEffect(() => {
    if (ledgerForm.type !== 'Customer' || isEditing) return;
    const token = localStorage.getItem('token');
    axios
      .get(`${baseURL}/articles`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        const articles = Array.isArray(res.data) ? res.data : res.data?.data ?? [];
        const rate = pickGoldArticleRate(articles);
        if (rate == null) return;
        setLedgerForm((prev) => {
          if (prev.type !== 'Customer' || prev._id) return prev;
          const empty = prev.rateOfInterest === '' || prev.rateOfInterest == null;
          if (!empty) return prev;
          return { ...prev, rateOfInterest: String(rate) };
        });
      })
      .catch(() => {});
  }, [ledgerForm.type, isEditing]);



  const handleInputChange = (e) => {
    const { name, value, files, type } = e.target;
    
    if (type === 'file') {
      setLedgerForm(prev => ({
        ...prev,
        photos: {
          ...prev.photos,
          [name]: files[0] || null
        }
      }));
    } else {
      // Handle "Add New" selections
      if (value === '__ADD_NEW__') {
        if (name === 'caste') {
          setAddCasteModalOpen(true);
        } else if (name === 'village') {
          setAddVillageModalOpen(true);
        }
        return;
      }
      
      // Don't uppercase select/number fields — values must stay exact
      if (
        name === 'type' ||
        name === 'openingBalanceType' ||
        name === 'dhara' ||
        name === 'rateOfInterest' ||
        name === 'mobileNo'
      ) {
        setLedgerForm((prev) => ({
          ...prev,
          [name]: value,
          ...(name === 'type' && value === 'Customer' && !prev._id
            ? { dhara: prev.dhara || DEFAULT_DHARA }
            : {})
        }));
        return;
      }
      
      // Convert all other text inputs to uppercase; address also wraps every 30 chars per line (option B)
      const upperValue = value ? value.toUpperCase() : '';
      const finalValue =
        name === 'address' ? wrapAddressPerLineSegments(upperValue) : upperValue;
      setLedgerForm((prev) => ({
        ...prev,
        [name]: finalValue
      }));
    }
  };

  const validateForm = () => {
    return true;
  };

  const handleCreateLedger = async (e) => {
    e.preventDefault();
    setCreateLoading(true);
    setCreateError(null);


    // Validation disabled - proceed with form submission

    try {
      const token = localStorage.getItem('token');

      // ----- BUILD COMMON PAYLOAD (without photos) -----
      const basePayload = {};
      Object.keys(ledgerForm).forEach(key => {
        if (key === '_id' || key === 'photos') return;
        let value = ledgerForm[key];
        // Enum fields: never send "" — backend expects null / Debit / Credit
        if (key === 'openingBalanceType') {
          basePayload[key] = value === 'Debit' || value === 'Credit' ? value : null;
          return;
        }
        if (key === 'dhara') {
          basePayload[key] = value ? value : null;
          return;
        }
        if (key === 'rateOfInterest' || key === 'openingBalance') {
          if (value === '' || value == null) {
            basePayload[key] = key === 'openingBalance' ? 0 : null;
          } else {
            basePayload[key] = value;
          }
          return;
        }
        basePayload[key] = value == null ? '' : value;
      });

      // ID numbers from idNumbers / otherId fields
      if (selectedIdTypes.includes('aadhar')) {
        basePayload.aadharCardNo = String(idNumbers.aadhar ?? '');
      }
      if (selectedIdTypes.includes('pan')) {
        basePayload.panCardNo = String(idNumbers.pan ?? '');
      }
      if (selectedIdTypes.includes('voter')) {
        basePayload.voterIdNo = String(idNumbers.voter ?? '');
      }
      if (selectedIdTypes.includes('driving')) {
        basePayload.drivingLicenceNo = String(idNumbers.driving ?? '');
      }
      if (selectedIdTypes.includes('other')) {
        basePayload.otherIdName = String(otherIdName ?? '');
        basePayload.otherIdNumber = String(otherIdNumber ?? '');
      }

      // ----- EDIT MODE: PUT /ledgers/:id (multipart so photos can update) -----
      if (isEditing && ledgerForm._id) {

        const formData = new FormData();
        Object.keys(basePayload).forEach((key) => {
          let value = basePayload[key];
          if (value === null || value === undefined) value = '';
          formData.append(key, value);
        });

        const isUploadablePhoto = (v) =>
          !!v && typeof v !== 'string' && (v instanceof File || (typeof Blob !== 'undefined' && v instanceof Blob));

        if (ledgerForm.photos) {
          Object.keys(ledgerForm.photos).forEach((photoType) => {
            const photo = ledgerForm.photos[photoType];
            if (isUploadablePhoto(photo)) {
              formData.append(photoType, photo);
            }
          });
        }

        const response = await axios.put(
          `${baseURL}/ledgers/${ledgerForm._id}`,
          formData,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );

        const updated = response.data;

        // Update ledger list in-place
        setLedgers(prev =>
          prev.map(l => (l._id === updated._id ? { ...l, ...updated } : l))
        );

        setIsEditing(false);
        resetForm();
        setCreateSuccess(true);
        setCreateLoading(false);
        return;
      }

      // ----- CREATE MODE: POST /ledgers with multipart (supports photos) -----
      const formData = new FormData();

      // Basic fields
      Object.keys(basePayload).forEach(key => {
        let value = basePayload[key];
        if (value === null || value === undefined) value = '';
        formData.append(key, value);
      });

      // Photos if present
      if (ledgerForm.photos) {
        Object.keys(ledgerForm.photos).forEach(photoType => {
          if (ledgerForm.photos[photoType]) {
            formData.append(photoType, ledgerForm.photos[photoType]);
          }
        });
      }

      const response = await axios.post(`${baseURL}/ledgers`, formData, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      const newCustomer = response.data;
      
      // Call the callback to close modal and update parent (only for customer creation in pledge flow)
      if (onCustomerCreated) {
        onCustomerCreated(newCustomer);
      }
      
      if (onClose) {
        onClose();
      }
      
      setCreateSuccess(true);
      setCreateLoading(false);
      
      // Reset form after successful creation
      resetForm();
      
      // Refresh the ledger list (current page)
      const refreshResponse = await axios.get(`${baseURL}/ledgers`, { 
        params: {
          page: ledgerPage + 1,
          limit: ledgerRowsPerPage,
          ...(ledgerSearch.trim() ? { search: ledgerSearch.trim() } : {})
        },
        headers: { 'Authorization': `Bearer ${token}` } 
      });
      const res = refreshResponse.data;
      setLedgers(res.data || res);
      setLedgerTotalDocs(res.totalDocs ?? (res.data || res).length);
    } catch (err) {
      setCreateError(err.response?.data?.message || 'Failed to save ledger');
      setCreateLoading(false);
    }
  };

  const handleEditLedger = async (ledger) => {
 // Inspect the ledger object
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${baseURL}/ledgers/${ledger._id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const ledgerData = response.data;
 // Debug log

      // Create URLs for the photos
      const createPhotoUrl = (filename) => {
        const url = resolvePledgePhotoUrl(filename);
        return url;
      };

      const photoUrls = {
        aadharCard: createPhotoUrl(ledgerData.photos?.aadharCard),
        panCard: createPhotoUrl(ledgerData.photos?.panCard),
        clientPhoto: createPhotoUrl(ledgerData.photos?.clientPhoto)
      };

 // Debug log

      // Test image URLs
      Object.entries(photoUrls).forEach(([key, url]) => {
        if (url) {
          fetch(url)
            .then(response => {
              if (!response.ok) {
              }
            })
            .catch(() => {});
        }
      });

      setLedgerForm({
        _id: ledgerData._id,
        type: ledgerData.type,
        firstName: ledgerData.firstName,
        lastName: ledgerData.lastName,
        address: wrapAddressPerLineSegments(
          (ledgerData.address ?? '').toString().toUpperCase()
        ),
        caste: ledgerData.caste,
        occupation: ledgerData.occupation,
        mobileNo: ledgerData.mobileNo,
        village: ledgerData.village,
        atPost: ledgerData.atPost,
        taluka: ledgerData.taluka,
        district: ledgerData.district,
        panCardNo: ledgerData.panCardNo,
        aadharCardNo: ledgerData.aadharCardNo,
        groupCode: ledgerData.groupCode,
        openingBalanceType: ledgerData.openingBalanceType || '',
        openingBalance: ledgerData.openingBalance || '',
        rateOfInterest: ledgerData.rateOfInterest ?? '',
        dhara: ledgerData.dhara || DEFAULT_DHARA,
        photos: photoUrls
      });

      // Store photo URLs in localStorage
      localStorage.setItem('ledgerPhotos', JSON.stringify(photoUrls));
      setIsEditing(true);
    } catch (error) {
      setCreateError('Failed to load ledger details');
    }
  };

  const handleDeleteLedger = async () => {
    if (!ledgerToDelete) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${baseURL}/ledgers/${ledgerToDelete._id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      setDeleteDialogOpen(false);
      setLedgerToDelete(null);
      setCreateSuccess(true);
      fetchLedgersPage(ledgerPage, ledgerRowsPerPage, ledgerSearch);
    } catch (err) {
      setCreateError(err.response?.data?.message || 'Failed to delete ledger');
    }
  };

  const handleCloseSnackbar = () => {
    setCreateSuccess(false);
    setCreateError(null);
  };

  const handlePhotoCapture = (file, photoType) => {
    setLedgerForm(prev => ({
      ...prev,
      photos: {
        ...prev.photos,
        [photoType]: file
      }
    }));
  };

  const handleFileUpload = (file, photoType) => {
    setLedgerForm(prev => ({
      ...prev,
      photos: {
        ...prev.photos,
        [photoType]: file
      }
    }));
  };

  const resetForm = () => {
    setLedgerForm({
      _id: '',
      type: 'Customer',
      firstName: '',
      lastName: '',
      relation: '',
      relationName: '',
      address: '',
      caste: '',
      occupation: '',
      mobileNo: '',
      village: '',
      atPost: '',
      taluka: '',
      district: '',
      panCardNo: '',
      aadharCardNo: '',
      groupCode: '',
      openingBalanceType: '',
      openingBalance: '',
      rateOfInterest: '',
      dhara: DEFAULT_DHARA,
      photos: {
        aadharCard: null,
        panCard: null,
        clientPhoto: null
      }
    });
    setIsEditing(false);
    setSelectedIdTypes([]);
    setModalIdPicks([]);
    setIdNumbers({
      aadhar: '',
      pan: '',
      voter: '',
      driving: ''
    });
    setOtherIdName('');
    setOtherIdNumber('');
    setShowOtherFields(false);
    resetPhotoPreviews();
  };

  const resetPhotoPreviews = () => {
    // Reset preview images in form
    setLedgerForm(prev => ({
      ...prev,
      photos: {
        aadharCard: null,
        panCard: null,
        clientPhoto: null
      }
    }));

    // Clear photos from localStorage
    localStorage.removeItem('ledgerPhotos');
  };

  const renderCustomerDharaRateFields = () => (
    <Grid container spacing={2} sx={{ mt: 0 }}>
      <Grid item xs={12} sm={6}>
        <TextField
          margin="normal"
          fullWidth
          id="rateOfInterest"
          label="Rate of Interest (% p.a.)"
          name="rateOfInterest"
          type="number"
          inputProps={{ min: 0, max: 100, step: 0.01 }}
          value={ledgerForm.rateOfInterest}
          onChange={handleInputChange}
          helperText="Defaults from Gold article in Article Master"
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          margin="normal"
          required
          fullWidth
          select
          id="dhara"
          label="Dhara"
          name="dhara"
          value={ledgerForm.dhara || DEFAULT_DHARA}
          onChange={handleInputChange}
          SelectProps={{ native: true }}
        >
          {DHARA_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </TextField>
      </Grid>
    </Grid>
  );

  if (isModal) {
    return (
      <Box
        component="form"
        onSubmit={handleCreateLedger}
        noValidate
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(4, minmax(0, 1fr))' },
          columnGap: 1.25,
          rowGap: 0.75,
          alignItems: 'start',
          pt: 0.5,
          '& > .MuiTextField-root:first-of-type': { display: 'none' },
          '& .MuiFormControl-root': { marginTop: '8px', marginBottom: 0, minWidth: 0 },
          '& .MuiFormHelperText-root': { display: 'none' },
          '& .MuiInputLabel-root': {
            zIndex: 2,
            color: '#334155',
            pointerEvents: 'none',
            fontSize: 13,
            backgroundColor: '#f8fafc',
            paddingLeft: '4px',
            paddingRight: '4px',
          },
          '& .MuiOutlinedInput-root': { background: '#fff', minHeight: 40 },
          '& .MuiOutlinedInput-input': { fontSize: 13, paddingTop: '8.5px', paddingBottom: '8.5px' },
          '& .MuiGrid-container': { gridColumn: '1 / -1' },
        }}
      >
        <TextField
          margin="normal"
          select
          required
          fullWidth
          id="type"
          label="Type"
          name="type"
          value={ledgerForm.type}
          onChange={handleInputChange}
          SelectProps={{ native: true }}
        >
          {['Customer', 'Supplier', 'General'].map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </TextField>
        {/* Group Code - Required for General, Optional for Supplier */}
        {(ledgerForm.type === 'General' || ledgerForm.type === 'Supplier') && (
          <TextField
            margin="normal"
            select
            required={ledgerForm.type === 'General'}
            fullWidth
            id="groupCode"
            label="Group Code"
            name="groupCode"
            value={ledgerForm.groupCode}
            onChange={handleInputChange}
            SelectProps={{ native: true }}
          >
            <option value="">Select Group Code</option>
            {sortedGroupCodes.map((groupCode) => (
              <option key={groupCode._id} value={groupCode.code}>
                {groupCode.code} - {groupCode.name}
              </option>
            ))}
          </TextField>
        )}
        
        {/* Customer Fields */}
        {ledgerForm.type === 'Customer' && (
          <>
            <Box sx={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: 'minmax(0,1.2fr) minmax(0,1.2fr) 120px minmax(0,1fr)', gap: 1.25 }}>
              <TextField
                size="small"
                required
                fullWidth
                label="First Name"
                name="firstName"
                value={ledgerForm.firstName}
                onChange={handleInputChange}
              />
              <TextField
                size="small"
                required
                fullWidth
                label="Last Name"
                name="lastName"
                value={ledgerForm.lastName}
                onChange={handleInputChange}
              />
              <TextField
                size="small"
                select
                fullWidth
                label="Relation"
                value={String(ledgerForm.relation || '').toLowerCase() === 'f/o' ? '' : (ledgerForm.relation || '')}
                onChange={(e) => setLedgerForm((prev) => ({ ...prev, relation: e.target.value }))}
                SelectProps={{ native: true }}
              >
                <option value="">Select</option>
                <option value="d/o">D/O</option>
                <option value="s/o">S/O</option>
                <option value="c/o">C/O</option>
                <option value="w/o">W/O</option>
              </TextField>
              <TextField
                size="small"
                fullWidth
                label="Name"
                value={ledgerForm.relationName || ''}
                onChange={(e) => setLedgerForm((prev) => ({ ...prev, relationName: e.target.value.toUpperCase() }))}
                placeholder="e.g. manish"
              />
            </Box>
            <TextField
              size="small"
              required
              fullWidth
              label="Address"
              name="address"
              value={ledgerForm.address}
              onChange={handleInputChange}
              sx={{ gridColumn: '1 / -1' }}
            />
          </>
        )}
        
        {/* Supplier and General Fields */}
        {(ledgerForm.type === 'Supplier' || ledgerForm.type === 'General') && (
          <>
            <TextField
              margin="normal"
              required
              fullWidth
              id="firstName"
              label="Ledger Name"
              name="firstName"
              value={ledgerForm.firstName}
              onChange={handleInputChange}
              placeholder="e.g., Interest Income, Electricity Expense, Bank Account"
              inputProps={{
                style: { textTransform: 'uppercase' }
              }}
            />
            <TextField
              margin="normal"
              fullWidth
              multiline
              rows={3}
              id="address"
              label="Description"
              name="address"
              value={ledgerForm.address}
              onChange={handleInputChange}
              placeholder="Enter description for this ledger..."
              inputProps={{
                style: { textTransform: 'uppercase' }
              }}
            />
          </>
        )}
        
        {/* Opening Balance - Only for General Ledger */}
        {ledgerForm.type === 'General' && (
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <TextField
                select
                fullWidth
                id="openingBalanceType"
                label="Opening Balance Type"
                name="openingBalanceType"
                value={ledgerForm.openingBalanceType}
                onChange={handleInputChange}
                SelectProps={{ native: true }}
              >
                <option value="">-- Select --</option>
                <option value="Debit">Debit</option>
                <option value="Credit">Credit</option>
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                id="openingBalance"
                label="Opening Balance Amount"
                name="openingBalance"
                type="number"
                value={ledgerForm.openingBalance}
                onChange={handleInputChange}
                placeholder="0.00"
              />
            </Grid>
          </Grid>
        )}
        {/* Customer-specific fields */}
        {ledgerForm.type === 'Customer' && (
          <>
            <TextField
              size="small"
              fullWidth
              label="Rate of Interest (% p.a.)"
              name="rateOfInterest"
              type="number"
              inputProps={{ min: 0, max: 100, step: 0.01 }}
              value={ledgerForm.rateOfInterest}
              onChange={handleInputChange}
            />
            <TextField
              size="small"
              required
              select
              fullWidth
              label="Dhara"
              name="dhara"
              value={ledgerForm.dhara || DEFAULT_DHARA}
              onChange={handleInputChange}
              SelectProps={{ native: true }}
            >
              {DHARA_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </TextField>
            <Autocomplete
              margin="normal"
              fullWidth
              freeSolo
              openOnFocus={false}
              id="caste"
              options={[...sortedCastes.map((c) => c.name), '__ADD_NEW__']}
              getOptionLabel={(opt) => (opt === '__ADD_NEW__' ? '+ Add New Caste' : opt)}
              filterOptions={(opts, state) => {
                const filtered = opts.filter((o) => o !== '__ADD_NEW__' && (state.inputValue === '' || (o && o.toUpperCase().includes((state.inputValue || '').toUpperCase()))));
                return [...filtered, '__ADD_NEW__'];
              }}
              value={ledgerForm.caste || ''}
              inputValue={ledgerForm.caste ?? ''}
              onInputChange={(e, v) => setLedgerForm((prev) => ({ ...prev, caste: (v || '').toUpperCase() }))}
              onChange={(e, val) => {
                if (val === '__ADD_NEW__') {
                  setAddCasteModalOpen(true);
                  return;
                }
                setLedgerForm((prev) => ({ ...prev, caste: (val || '').toUpperCase() }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label="Caste"
                  required
                  inputProps={{
                    ...params.inputProps,
                    style: { textTransform: 'uppercase' }
                  }}
                />
              )}
              renderOption={(props, option) =>
                option === '__ADD_NEW__' ? (
                  <li
                    {...props}
                    key="__ADD_NEW__"
                    onClick={() => setAddCasteModalOpen(true)}
                    style={{ color: 'var(--mui-palette-primary-main)', fontWeight: 'bold' }}
                  >
                    <AddIcon sx={{ mr: 1, fontSize: 'small' }} />
                    Add New Caste
                  </li>
                ) : (
                  <li {...props} key={option}>
                    {option}
                  </li>
                )
              }
              ListboxProps={{
                style: { maxHeight: 280 }
              }}
              componentsProps={{ popper: { sx: { zIndex: 100000 } } }}
            />
            <Autocomplete
              freeSolo
              openOnFocus={false}
              options={occupationSuggestions}
              value={ledgerForm.occupation || ''}
              inputValue={ledgerForm.occupation ?? ''}
              onInputChange={(e, v) => setLedgerForm((prev) => ({ ...prev, occupation: (v || '').toUpperCase() }))}
              onChange={(e, val) => setLedgerForm((prev) => ({ ...prev, occupation: (typeof val === 'string' ? val : val || '').toUpperCase() }))}
              componentsProps={{ popper: { sx: { zIndex: 100000 } } }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label="Occupation"
                  fullWidth
                />
              )}
            />
            <TextField
              size="small"
              required
              fullWidth
              id="mobileNo"
              label="Mobile Number"
              name="mobileNo"
              value={ledgerForm.mobileNo}
              onChange={handleInputChange}
              sx={{ gridColumn: '1 / 2' }}
            />
            <FormControl size="small" sx={{ gridColumn: '1 / -1', maxWidth: 460 }}>
              <InputLabel id="modal-id-label">ID</InputLabel>
              <Select
                labelId="modal-id-label"
                multiple
                value={modalIdPicks}
                onChange={handleModalIdPicks}
                input={<OutlinedInput label="ID" />}
                renderValue={(selected) => (
                  selected.length
                    ? MODAL_ID_OPTIONS.filter((opt) => selected.includes(opt.value)).map((opt) => opt.label).join(', ')
                    : ''
                )}
                MenuProps={{ sx: { zIndex: 100000 }, PaperProps: { sx: { maxHeight: 320 } } }}
              >
                {MODAL_ID_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    <Checkbox size="small" checked={modalIdPicks.indexOf(opt.value) > -1} />
                    <ListItemText primary={opt.label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {selectedIdTypes.includes('aadhar') && (
              <TextField
                size="small"
                fullWidth
                label="Aadhar Card Number"
                value={idNumbers.aadhar}
                onChange={handleIdNumberChange('aadhar')}
                sx={{ gridColumn: '1 / -1', maxWidth: 280 }}
              />
            )}
            {selectedIdTypes.includes('pan') && (
              <TextField
                size="small"
                fullWidth
                label="Pancard Number"
                value={idNumbers.pan}
                onChange={handleIdNumberChange('pan')}
                sx={{ gridColumn: '1 / -1', maxWidth: 280 }}
              />
            )}
            {selectedIdTypes.includes('voter') && (
              <TextField
                size="small"
                fullWidth
                label="Voter ID Number"
                value={idNumbers.voter}
                onChange={handleIdNumberChange('voter')}
                sx={{ gridColumn: '1 / -1', maxWidth: 280 }}
              />
            )}
            {selectedIdTypes.includes('driving') && (
              <TextField
                size="small"
                fullWidth
                label="Driving Licence Number"
                value={idNumbers.driving}
                onChange={handleIdNumberChange('driving')}
                sx={{ gridColumn: '1 / -1', maxWidth: 280 }}
              />
            )}
            {showOtherFields && (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Other ID Name"
                    value={otherIdName}
                    onChange={handleOtherIdNameChange}
                    margin="normal"
                    inputProps={{
                      style: { textTransform: 'uppercase' }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Other ID Number"
                    value={otherIdNumber}
                    onChange={handleOtherIdNumberChange}
                    margin="normal"
                    inputProps={{
                      style: { textTransform: 'uppercase' }
                    }}
                  />
                </Grid>
              </Grid>
            )}
            <TextField
              size="small"
              select
              fullWidth
              label="Village"
              name="village"
              value={ledgerForm.village}
              onChange={handleInputChange}
              SelectProps={{ native: true }}
            >
              <option value="">Select</option>
              {uniqueVillagesSorted.map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
              <option value="__ADD_NEW__">Add New Village</option>
            </TextField>
            <TextField
              size="small"
              select
              fullWidth
              label="At Post"
              name="atPost"
              value={ledgerForm.atPost}
              onChange={handleInputChange}
              SelectProps={{ native: true }}
            >
              <option value="">Select</option>
              {uniqueAtPostsSorted.map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
            </TextField>
            <TextField
              size="small"
              select
              fullWidth
              label="Taluka"
              name="taluka"
              value={ledgerForm.taluka}
              onChange={handleInputChange}
              SelectProps={{ native: true }}
            >
              <option value="">Select</option>
              {uniqueTalukasSorted.map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
            </TextField>
            <TextField
              size="small"
              select
              fullWidth
              label="District"
              name="district"
              value={ledgerForm.district}
              onChange={handleInputChange}
              SelectProps={{ native: true }}
            >
              <option value="">Select</option>
              {uniqueDistrictsSorted.map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
            </TextField>
          </>
        )}
        
        {/* Mobile Number for Supplier and General (optional) */}
        {(ledgerForm.type === 'Supplier' || ledgerForm.type === 'General') && (
          <TextField
            margin="normal"
            fullWidth
            id="mobileNo"
            label="Mobile Number (Optional)"
            name="mobileNo"
            value={ledgerForm.mobileNo}
            onChange={handleInputChange}
          />
        )}
        {ledgerForm.type !== 'Customer' && (
        <>
        <TextField
          margin="normal"
          select
          required
          fullWidth
          id="taluka"
          label="Taluka"
          name="taluka"
          value={ledgerForm.taluka}
          onChange={handleInputChange}
          InputLabelProps={{ shrink: true }}
          SelectProps={{ native: true }}
        >
          <option value="">Select Taluka</option>
          {uniqueTalukasSorted.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </TextField>
        <TextField
          margin="normal"
          select
          required
          fullWidth
          id="district"
          label="District"
          name="district"
          value={ledgerForm.district}
          onChange={handleInputChange}
          InputLabelProps={{ shrink: true }}
          SelectProps={{ native: true }}
        >
          <option value="">Select District</option>
          {uniqueDistrictsSorted.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </TextField>
        <Box sx={{ gridColumn: '1 / -1', width: '100%', mt: 1 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs 
              value={photoUploadTab} 
              onChange={(e, newValue) => setPhotoUploadTab(newValue)}
              variant="fullWidth"
            >
              <Tab label="Aadhar Card" />
              <Tab label="PAN Card" />
              <Tab label="Client Photo" />
            </Tabs>
          </Box>
          
          <TabPanel value={photoUploadTab} index={0}>
            <PhotoUploadComponent 
              photoType="aadharCard"
              onPhotoCapture={handlePhotoCapture}
              onFileUpload={handleFileUpload}
              existingPhotos={ledgerForm.photos}
            />
          </TabPanel>
          
          <TabPanel value={photoUploadTab} index={1}>
            <PhotoUploadComponent 
              photoType="panCard"
              onPhotoCapture={handlePhotoCapture}
              onFileUpload={handleFileUpload}
              existingPhotos={ledgerForm.photos}
            />
          </TabPanel>
          
          <TabPanel value={photoUploadTab} index={2}>
            <PhotoUploadComponent 
              photoType="clientPhoto"
              onPhotoCapture={handlePhotoCapture}
              onFileUpload={handleFileUpload}
              existingPhotos={ledgerForm.photos}
            />
          </TabPanel>
        </Box>
        </>
        )}

        <Box sx={{ gridColumn: '1 / -1', mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            type="submit"
            disabled={createLoading}
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              borderRadius: 2,
              px: 2.5,
              background: 'linear-gradient(180deg, #1e3a5f, #0b1220)',
              '&:hover': { background: 'linear-gradient(180deg, #2a4d78, #122038)' }
            }}
          >
            {createLoading ? <CircularProgress size={18} sx={{ color: '#f5e6c8' }} /> : 'Save Customer'}
          </Button>
        </Box>
        
        {/* Add New Modals */}
        <Dialog 
          open={addCasteModalOpen} 
          onClose={() => setAddCasteModalOpen(false)} 
          maxWidth="sm" 
          fullWidth
          disableRestoreFocus
          keepMounted={false}
        >
          <DialogTitle>Add New Caste</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Caste Name"
              type="text"
              fullWidth
              variant="outlined"
              value={newCasteForm.name}
              onChange={(e) => setNewCasteForm({ name: e.target.value })}
              sx={{ mt: 2 }}
              inputProps={{
                'aria-label': 'Caste name input',
                style: { textTransform: 'uppercase' }
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddCasteModalOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleAddCaste} 
              variant="contained" 
              disabled={casteLoading}
            >
              {casteLoading ? <CircularProgress size={20} /> : 'Add Caste'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog 
          open={addVillageModalOpen} 
          onClose={() => setAddVillageModalOpen(false)} 
          maxWidth="sm" 
          fullWidth
          disableRestoreFocus
          keepMounted={false}
        >
          <DialogTitle>Add New Village</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Village"
              type="text"
              fullWidth
              variant="outlined"
              value={newVillageForm.village}
              onChange={(e) => setNewVillageForm(prev => ({ ...prev, village: e.target.value }))}
              sx={{ mt: 2 }}
              inputProps={{
                style: { textTransform: 'uppercase' }
              }}
            />
            <TextField
              margin="dense"
              label="At Post"
              type="text"
              fullWidth
              variant="outlined"
              value={newVillageForm.atPost}
              onChange={(e) => setNewVillageForm(prev => ({ ...prev, atPost: e.target.value }))}
              inputProps={{
                style: { textTransform: 'uppercase' }
              }}
            />
            <TextField
              margin="dense"
              label="Taluka"
              type="text"
              fullWidth
              variant="outlined"
              value={newVillageForm.taluka}
              onChange={(e) => setNewVillageForm(prev => ({ ...prev, taluka: e.target.value }))}
              inputProps={{
                style: { textTransform: 'uppercase' }
              }}
            />
            <TextField
              margin="dense"
              label="District"
              type="text"
              fullWidth
              variant="outlined"
              value={newVillageForm.district}
              onChange={(e) => setNewVillageForm(prev => ({ ...prev, district: e.target.value }))}
              inputProps={{
                style: { textTransform: 'uppercase' }
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setAddVillageModalOpen(false);
            }}>Cancel</Button>
            <Button 
              onClick={handleAddVillage} 
              variant="contained" 
              disabled={villageLoading}
            >
              {villageLoading ? <CircularProgress size={20} /> : 'Add Village'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }



  if (loading) return <CircularProgress />;
  if (error) return <Typography color="error">{error}</Typography>;

  return (
    <MasterPageShell title="Ledgers" subtitle="Master · Accounts">
        <Grid container spacing={3}>
          {/* Create/Edit Ledger Form */}
          <Grid item xs={12} md={4}>
            <Paper elevation={0} sx={masterPaperSx}>
              <Typography variant="h6" gutterBottom>
                {isEditing ? 'Edit Ledger' : 'Create Ledger'}
              </Typography>
              <Box component="form" onSubmit={handleCreateLedger} noValidate>
                <TextField
                  margin="normal"
                  select
                  required
                  fullWidth
                  id="type"
                  label="Type"
                  name="type"
                  value={ledgerForm.type}
                  onChange={handleInputChange}
                  SelectProps={{ native: true }}
                >
                  {['Customer', 'Supplier', 'General'].map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </TextField>
                {/* Group Code - Required for General, Optional for Supplier */}
                {(ledgerForm.type === 'General' || ledgerForm.type === 'Supplier') && (
                  <TextField
                    margin="normal"
                    select
                    required={ledgerForm.type === 'General'}
                    fullWidth
                    id="groupCode"
                    label="Group Code"
                    name="groupCode"
                    value={ledgerForm.groupCode}
                    onChange={handleInputChange}
                    SelectProps={{ native: true }}
                  >
                    <option value="">Select Group Code</option>
                    {sortedGroupCodes.map((groupCode) => (
                      <option key={groupCode._id} value={groupCode.code}>
                        {groupCode.code} - {groupCode.name}
                      </option>
                    ))}
                  </TextField>
                )}
                
                {/* Customer Fields */}
                {ledgerForm.type === 'Customer' && (
                  <>
                    <TextField
                      margin="normal"
                      required
                      fullWidth
                      id="firstName"
                      label="First Name"
                      name="firstName"
                      value={ledgerForm.firstName}
                      onChange={handleInputChange}
                      inputProps={{
                        style: { textTransform: 'uppercase' }
                      }}
                    />
                    <TextField
                      margin="normal"
                      required
                      fullWidth
                      id="lastName"
                      label="Last Name"
                      name="lastName"
                      value={ledgerForm.lastName}
                      onChange={handleInputChange}
                      inputProps={{
                        style: { textTransform: 'uppercase' }
                      }}
                    />
                    <TextField
                      margin="normal"
                      required
                      fullWidth
                      multiline
                      rows={3}
                      id="address"
                      label="Address"
                      name="address"
                      value={ledgerForm.address}
                      onChange={handleInputChange}
                      inputProps={{
                        style: { textTransform: 'uppercase' }
                      }}
                    />
                    {renderCustomerDharaRateFields()}
                    <Autocomplete
                      margin="normal"
                      fullWidth
                      freeSolo
                      openOnFocus={false}
                      id="caste-edit"
                      options={[...sortedCastes.map((c) => c.name), '__ADD_NEW__']}
                      getOptionLabel={(opt) => (opt === '__ADD_NEW__' ? '+ Add New Caste' : opt)}
                      filterOptions={(opts, state) => {
                        const filtered = opts.filter((o) => o !== '__ADD_NEW__' && (state.inputValue === '' || (o && o.toUpperCase().includes((state.inputValue || '').toUpperCase()))));
                        return [...filtered, '__ADD_NEW__'];
                      }}
                      value={ledgerForm.caste || ''}
                      inputValue={ledgerForm.caste ?? ''}
                      onInputChange={(e, v) => setLedgerForm((prev) => ({ ...prev, caste: (v || '').toUpperCase() }))}
                      onChange={(e, val) => {
                        if (val === '__ADD_NEW__') {
                          setAddCasteModalOpen(true);
                          return;
                        }
                        setLedgerForm((prev) => ({ ...prev, caste: (val || '').toUpperCase() }));
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Caste"
                          required
                          margin="normal"
                          inputProps={{
                            ...params.inputProps,
                            style: { textTransform: 'uppercase' }
                          }}
                        />
                      )}
                      renderOption={(props, option) =>
                        option === '__ADD_NEW__' ? (
                          <li
                            {...props}
                            key="__ADD_NEW__"
                            onClick={() => setAddCasteModalOpen(true)}
                            style={{ color: 'var(--mui-palette-primary-main)', fontWeight: 'bold' }}
                          >
                            <AddIcon sx={{ mr: 1, fontSize: 'small' }} />
                            Add New Caste
                          </li>
                        ) : (
                          <li {...props} key={option}>
                            {option}
                          </li>
                        )
                      }
                      ListboxProps={{
                        style: { maxHeight: 280 }
                      }}
                    />
                    <Box sx={{ mt: 0.5 }}>
                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => setAddCasteModalOpen(true)}
                        sx={{ color: 'primary.main', fontWeight: 'bold', textTransform: 'none' }}
                      >
                        Add New Caste
                      </Button>
                    </Box>
                    <Autocomplete
                      freeSolo
                      openOnFocus={false}
                      options={occupationSuggestions}
                      value={ledgerForm.occupation || ''}
                      inputValue={ledgerForm.occupation ?? ''}
                      onInputChange={(e, v) => setLedgerForm((prev) => ({ ...prev, occupation: (v || '').toUpperCase() }))}
                      onChange={(e, val) => setLedgerForm((prev) => ({ ...prev, occupation: (typeof val === 'string' ? val : val || '').toUpperCase() }))}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Occupation"
                          margin="normal"
                          fullWidth
                          inputProps={{
                            ...params.inputProps,
                            style: { textTransform: 'uppercase' }
                          }}
                        />
                      )}
                    />
                    <TextField
                      margin="normal"
                      required
                      fullWidth
                      id="mobileNo"
                      label="Mobile Number"
                      name="mobileNo"
                      value={ledgerForm.mobileNo}
                      onChange={handleInputChange}
                    />
                  </>
                )}
                
                {/* Supplier and General Fields */}
                {(ledgerForm.type === 'Supplier' || ledgerForm.type === 'General') && (
                  <>
                    <TextField
                      margin="normal"
                      required
                      fullWidth
                      id="firstName"
                      label="Ledger Name"
                      name="firstName"
                      value={ledgerForm.firstName}
                      onChange={handleInputChange}
                      placeholder="e.g., Interest Income, Electricity Expense, Bank Account"
                      inputProps={{
                        style: { textTransform: 'uppercase' }
                      }}
                    />
                    <TextField
                      margin="normal"
                      fullWidth
                      multiline
                      rows={3}
                      id="address"
                      label="Description"
                      name="address"
                      value={ledgerForm.address}
                      onChange={handleInputChange}
                      placeholder="Enter description for this ledger..."
                      inputProps={{
                        style: { textTransform: 'uppercase' }
                      }}
                    />
                    <TextField
                      margin="normal"
                      fullWidth
                      id="mobileNo"
                      label="Mobile Number (Optional)"
                      name="mobileNo"
                      value={ledgerForm.mobileNo}
                      onChange={handleInputChange}
                    />
                    
                    {/* Opening Balance - Only for General Ledger */}
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                      <Grid item xs={6}>
                        <TextField
                          select
                          fullWidth
                          id="openingBalanceType"
                          label="Opening Balance Type"
                          name="openingBalanceType"
                          value={ledgerForm.openingBalanceType}
                          onChange={handleInputChange}
                          SelectProps={{ native: true }}
                        >
                          <option value="">-- Select --</option>
                          <option value="Debit">Debit</option>
                          <option value="Credit">Credit</option>
                        </TextField>
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          id="openingBalance"
                          label="Opening Balance Amount"
                          name="openingBalance"
                          type="number"
                          value={ledgerForm.openingBalance}
                          onChange={handleInputChange}
                          placeholder="0.00"
                        />
                      </Grid>
                    </Grid>
                  </>
                )}
                
                {/* Customer-specific ID Types and Address Fields */}
                {ledgerForm.type === 'Customer' && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel id="id-type-label">ID Types</InputLabel>
                        <Select
                          labelId="id-type-label"
                          multiple
                          value={selectedIdTypes}
                          onChange={handleIdTypeChange}
                          input={<OutlinedInput label="ID Types" />}
                          renderValue={(selected) => selected.join(', ')}
                        >
                          {idTypeOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              <Checkbox checked={selectedIdTypes.indexOf(option.value) > -1} />
                              <ListItemText primary={option.label} />
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    {selectedIdTypes.includes('aadhar') && (
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Aadhar Card Number"
                          value={idNumbers.aadhar}
                          onChange={handleIdNumberChange('aadhar')}
                          margin="normal"
                          inputProps={{
                            style: { textTransform: 'uppercase' }
                          }}
                        />
                      </Grid>
                    )}
                    {selectedIdTypes.includes('pan') && (
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Pancard Number"
                          value={idNumbers.pan}
                          onChange={handleIdNumberChange('pan')}
                          margin="normal"
                          inputProps={{
                            style: { textTransform: 'uppercase' }
                          }}
                        />
                      </Grid>
                    )}
                    {selectedIdTypes.includes('voter') && (
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Voter ID Number"
                          value={idNumbers.voter}
                          onChange={handleIdNumberChange('voter')}
                          margin="normal"
                          inputProps={{
                            style: { textTransform: 'uppercase' }
                          }}
                        />
                      </Grid>
                    )}
                    {selectedIdTypes.includes('driving') && (
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Driving Licence Number"
                          value={idNumbers.driving}
                          onChange={handleIdNumberChange('driving')}
                          margin="normal"
                          inputProps={{
                            style: { textTransform: 'uppercase' }
                          }}
                        />
                      </Grid>
                    )}
                    {showOtherFields && (
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Other ID Name"
                            value={otherIdName}
                            onChange={handleOtherIdNameChange}
                            margin="normal"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Other ID Number"
                            value={otherIdNumber}
                            onChange={handleOtherIdNumberChange}
                            margin="normal"
                          />
                        </Grid>
                      </Grid>
                    )}
                    <FormControl margin="normal" fullWidth required>
                      <InputLabel id="village-label-edit" shrink>
                        Village
                      </InputLabel>
                      <Select
                        labelId="village-label-edit"
                        id="village"
                        name="village"
                        value={ledgerForm.village}
                        onChange={handleInputChange}
                        label="Village"
                        displayEmpty
                        notched
                      >
                        <MenuItem value="">
                          <em>Select Village</em>
                        </MenuItem>
                        {sortedTownVillagesByVillage.map((tv) => (
                          <MenuItem key={tv._id} value={tv.village}>
                            {tv.village}
                          </MenuItem>
                        ))}
                        <MenuItem value="__ADD_NEW__" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                          <AddIcon sx={{ mr: 1, fontSize: 'small' }} />
                          Add New Village
                        </MenuItem>
                      </Select>
                    </FormControl>
                    <TextField
                      margin="normal"
                      select
                      required
                      fullWidth
                      id="atPost"
                      label="At Post"
                      name="atPost"
                      value={ledgerForm.atPost}
                      onChange={handleInputChange}
                      InputLabelProps={{ shrink: true }}
                      SelectProps={{ native: true }}
                    >
                      <option value="">Select At Post</option>
                      {uniqueAtPostsSorted.map((name) => (
                        <option key={name} value={name}>
                          {name}
                        </option>
                      ))}
                    </TextField>
                    <TextField
                      margin="normal"
                      select
                      required
                      fullWidth
                      id="taluka"
                      label="Taluka"
                      name="taluka"
                      value={ledgerForm.taluka}
                      onChange={handleInputChange}
                      InputLabelProps={{ shrink: true }}
                      SelectProps={{ native: true }}
                    >
                      <option value="">Select Taluka</option>
                      {uniqueTalukasSorted.map((name) => (
                        <option key={name} value={name}>
                          {name}
                        </option>
                      ))}
                    </TextField>
                    <TextField
                      margin="normal"
                      select
                      required
                      fullWidth
                      id="district"
                      label="District"
                      name="district"
                      value={ledgerForm.district}
                      onChange={handleInputChange}
                      InputLabelProps={{ shrink: true }}
                      SelectProps={{ native: true }}
                    >
                      <option value="">Select District</option>
                      {uniqueDistrictsSorted.map((name) => (
                        <option key={name} value={name}>
                          {name}
                        </option>
                      ))}
                    </TextField>
                  </>
                )}
                <Box sx={{ width: '100%', mt: 2 }}>
                  <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs 
                      value={photoUploadTab} 
                      onChange={(e, newValue) => setPhotoUploadTab(newValue)}
                      variant="fullWidth"
                    >
                      <Tab label="Aadhar Card" />
                      <Tab label="PAN Card" />
                      <Tab label="Client Photo" />
                    </Tabs>
                  </Box>
                  
                  <TabPanel value={photoUploadTab} index={0}>
                    <PhotoUploadComponent 
                      photoType="aadharCard"
                      onPhotoCapture={handlePhotoCapture}
                      onFileUpload={handleFileUpload}
                      existingPhotos={ledgerForm.photos}
                    />
                  </TabPanel>
                  
                  <TabPanel value={photoUploadTab} index={1}>
                    <PhotoUploadComponent 
                      photoType="panCard"
                      onPhotoCapture={handlePhotoCapture}
                      onFileUpload={handleFileUpload}
                      existingPhotos={ledgerForm.photos}
                    />
                  </TabPanel>
                  
                  <TabPanel value={photoUploadTab} index={2}>
                    <PhotoUploadComponent 
                      photoType="clientPhoto"
                      onPhotoCapture={handlePhotoCapture}
                      onFileUpload={handleFileUpload}
                      existingPhotos={ledgerForm.photos}
                    />
                  </TabPanel>
                </Box>
               
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="primary"
                  disabled={createLoading}
                  sx={{ mt: 2 }}
                >
                  {isEditing ? 'Update Ledger' : 'Create Ledger'}
                </Button>
                {isEditing && (
                  <Button
                    fullWidth
                    variant="outlined"
                    color="secondary"
                    onClick={() => {
                      setIsEditing(false);
                      resetForm();
                    }}
                    sx={{ mt: 1 }}
                  >
                    Cancel
                  </Button>
                )}
              </Box>
            </Paper>
          </Grid>

          {/* Ledgers List */}
          <Grid item xs={12} md={8}>
            <TableContainer component={Paper} elevation={3}>
              <Box sx={{ p: 2, pb: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Search ledgers"
                  placeholder="Type customer code or name (e.g. CAA001 or Ramesh)"
                  value={ledgerSearch}
                  onChange={(e) => setLedgerSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" color="action" />
                      </InputAdornment>
                    )
                  }}
                />
              </Box>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Code</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Group Code</TableCell>
                    <TableCell>Opening Balance</TableCell>
                    <TableCell>Mobile</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ledgers.map((ledger) => (
                    <TableRow key={ledger._id}>
                      <TableCell>{ledger.code || 'N/A'}</TableCell>
                      <TableCell>
                        {ledger.type === 'Customer' 
                          ? `${ledger.firstName || ''} ${ledger.lastName || ''}`.trim() || 'N/A'
                          : ledger.firstName || 'N/A'
                        }
                      </TableCell>
                      <TableCell>{ledger.type}</TableCell>
                      <TableCell>
                        {(ledger.type === 'General' || ledger.type === 'Supplier') 
                          ? (ledger.groupCode || 'N/A') 
                          : '-'
                        }
                      </TableCell>
                      <TableCell align="right">
                        {ledger.openingBalance ? 
                          `${ledger.openingBalanceType === 'Debit' ? 'DR' : 'CR'} ${ledger.openingBalance}` 
                          : '-'
                        }
                      </TableCell>
                      <TableCell>{ledger.mobileNo}</TableCell>
                      <TableCell align="right">
                        <IconButton 
                          color="primary" 
                          onClick={() => handleEditLedger(ledger)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton 
                          color="error" 
                          onClick={() => {
                            setLedgerToDelete(ledger);
                            setDeleteDialogOpen(true);
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={ledgerTotalDocs}
                page={ledgerPage}
                onPageChange={handleLedgerPageChange}
                rowsPerPage={ledgerRowsPerPage}
                onRowsPerPageChange={handleLedgerRowsPerPageChange}
                rowsPerPageOptions={[25, 50, 100, 200]}
                labelRowsPerPage="Rows per page:"
              />
            </TableContainer>
          </Grid>
        </Grid>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
        >
          <DialogTitle>Delete Ledger</DialogTitle>
          <DialogContent>
            Are you sure you want to delete this ledger?
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button 
              color="error" 
              onClick={handleDeleteLedger}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Success/Error Snackbars */}
        <Snackbar 
          open={createSuccess} 
          autoHideDuration={3000} 
          onClose={handleCloseSnackbar}
        >
          <Alert 
            onClose={handleCloseSnackbar} 
            severity="success" 
            sx={{ width: '100%' }}
          >
            Operation successful!
          </Alert>
        </Snackbar>

        <Snackbar 
          open={!!createError} 
          autoHideDuration={3000} 
          onClose={handleCloseSnackbar}
        >
          <Alert 
            onClose={handleCloseSnackbar} 
            severity="error" 
            sx={{ width: '100%' }}
          >
            {createError}
          </Alert>
        </Snackbar>

        {/* Add New Modals */}
        <Dialog 
          open={addCasteModalOpen} 
          onClose={() => setAddCasteModalOpen(false)} 
          maxWidth="sm" 
          fullWidth
          disableRestoreFocus
          keepMounted={false}
        >
          <DialogTitle>Add New Caste</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Caste Name"
              type="text"
              fullWidth
              variant="outlined"
              value={newCasteForm.name}
              onChange={(e) => setNewCasteForm({ name: e.target.value })}
              sx={{ mt: 2 }}
              inputProps={{
                'aria-label': 'Caste name input',
                style: { textTransform: 'uppercase' }
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddCasteModalOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleAddCaste} 
              variant="contained" 
              disabled={casteLoading}
            >
              {casteLoading ? <CircularProgress size={20} /> : 'Add Caste'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog 
          open={addVillageModalOpen} 
          onClose={() => setAddVillageModalOpen(false)} 
          maxWidth="sm" 
          fullWidth
          disableRestoreFocus
          keepMounted={false}
        >
          <DialogTitle>Add New Village</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Village"
              type="text"
              fullWidth
              variant="outlined"
              value={newVillageForm.village}
              onChange={(e) => setNewVillageForm(prev => ({ ...prev, village: e.target.value }))}
              sx={{ mt: 2 }}
              inputProps={{
                style: { textTransform: 'uppercase' }
              }}
            />
            <TextField
              margin="dense"
              label="At Post"
              type="text"
              fullWidth
              variant="outlined"
              value={newVillageForm.atPost}
              onChange={(e) => setNewVillageForm(prev => ({ ...prev, atPost: e.target.value }))}
              inputProps={{
                style: { textTransform: 'uppercase' }
              }}
            />
            <TextField
              margin="dense"
              label="Taluka"
              type="text"
              fullWidth
              variant="outlined"
              value={newVillageForm.taluka}
              onChange={(e) => setNewVillageForm(prev => ({ ...prev, taluka: e.target.value }))}
              inputProps={{
                style: { textTransform: 'uppercase' }
              }}
            />
            <TextField
              margin="dense"
              label="District"
              type="text"
              fullWidth
              variant="outlined"
              value={newVillageForm.district}
              onChange={(e) => setNewVillageForm(prev => ({ ...prev, district: e.target.value }))}
              inputProps={{
                style: { textTransform: 'uppercase' }
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddVillageModalOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleAddVillage} 
              variant="contained" 
              disabled={villageLoading}
            >
              {villageLoading ? <CircularProgress size={20} /> : 'Add Village'}
            </Button>
          </DialogActions>
        </Dialog>
    </MasterPageShell>
  );
};

export default LedgerMaster;